import type { SupabaseClient } from "@supabase/supabase-js";

// Client/site setup data layer (CONSOLE-SETUP-CLIENT-SITE-EDITOR). Reads: ONLY the hosted
// management_client_site_setup_list view (fadc4a3) - never base setup tables. The view is
// the safe allowlist (no timestamps/audit/guard ids/device ids/token data) and returns
// client rows even before a site exists (site_id null) so brand-new clients are editable.
// Writes: ONLY the six hosted RPCs from e6ee2cc - schedule.manage gated server-side,
// archive-over-delete via the active flags so history always survives. This completes the
// property chain: client -> site -> route -> checkpoints -> tokens -> schedule.

export interface ClientRow {
  clientId: string;
  name: string;
  slug: string | null;
  active: boolean;
}

export interface ManagedSite {
  siteId: string;
  clientId: string;
  name: string;
  streetAddress: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  timezone: string;
  centerLat: number | null;
  centerLng: number | null;
  active: boolean;
  /** Lifecycle gate (G5): setup sites are invisible to guards + fire no alerts until live. */
  status: string;
}

/** Address + timezone fields shared by site create and update. */
export interface SiteDraft {
  name: string;
  streetAddress: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  timezone: string;
  centerLat?: number | null;
  centerLng?: number | null;
}

export const CLIENT_SITE_SETUP_VIEW = "management_client_site_setup_list";

// One row per (client, site); a site-less client is one row with site_id null.
const LIST_LIMIT = 500;

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export interface ClientSiteSetup {
  clients: ClientRow[];
  sites: ManagedSite[];
}

/** Clients (deduped, incl. site-less ones) + sites from the setup view. Throws safe. */
export async function fetchClientSiteSetup(
  client: SupabaseClient,
  accountId: string,
): Promise<ClientSiteSetup> {
  const { data, error } = await client
    .from(CLIENT_SITE_SETUP_VIEW)
    .select("*")
    .eq("account_id", accountId)
    .order("client_name", { ascending: true })
    .limit(LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load clients and sites.");
  }
  const clientsById = new Map<string, ClientRow>();
  const sites: ManagedSite[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const clientId = asString(raw.client_id);
    if (clientId === null) continue;
    if (!clientsById.has(clientId)) {
      clientsById.set(clientId, {
        clientId,
        name: asString(raw.client_name) ?? "Client",
        slug: asString(raw.client_slug),
        active: raw.client_active === true,
      });
    }
    const siteId = asString(raw.site_id);
    if (siteId === null) continue;
    sites.push({
      siteId,
      clientId,
      name: asString(raw.site_name) ?? "Site",
      streetAddress: asString(raw.street_address),
      locality: asString(raw.locality),
      region: asString(raw.region),
      postalCode: asString(raw.postal_code),
      timezone: asString(raw.timezone) ?? "America/New_York",
      centerLat: asNumber(raw.center_lat),
      centerLng: asNumber(raw.center_lng),
      active: raw.site_active === true,
      status: asString(raw.site_status) ?? "setup",
    });
  }
  sites.sort((a, b) => a.name.localeCompare(b.name));
  return { clients: [...clientsById.values()], sites };
}

export const SAFE_CLIENT_SITE_WRITE_FAILURE = "Couldn't save that change - please try again.";

// Contract-stable, value-free sentences from the hosted client/site RPCs (20260611153731).
const KNOWN_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Active client was not found for this account.",
  "Archived clients cannot be updated.",
  "Archived sites cannot be updated.",
  "Client id is required to create sites.",
  "Client id is required to update sites.",
  "Client id is required to update clients.",
  "Client id is required to archive clients.",
  "Slug is required.",
  "Slug must use lowercase letters, numbers, and hyphens.",
  // Site lifecycle gate (G5, 20260611205716).
  "Active site was not found for this account.",
  "Site lifecycle status is not supported.",
]);

/** Lifecycle statuses the console may set (archive flows through archive_site, never here). */
export type SiteLifecycleStatus = "setup" | "live";

export type ClientSiteWriteOutcome =
  | { ok: true; clientId: string | null; siteId: string | null }
  | { ok: false; reason: string };

async function runWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<ClientSiteWriteOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_FAILURES.has(message) ? message : SAFE_CLIENT_SITE_WRITE_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    return {
      ok: true,
      clientId: typeof row?.client_id === "string" ? row.client_id : null,
      siteId: typeof row?.site_id === "string" ? row.site_id : null,
    };
  } catch {
    return { ok: false, reason: SAFE_CLIENT_SITE_WRITE_FAILURE };
  }
}

export async function createClient(
  client: SupabaseClient,
  accountId: string,
  name: string,
  slug: string | null,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "create_client", { p_account_id: accountId, p_name: name, p_slug: slug });
}

export async function updateClient(
  client: SupabaseClient,
  accountId: string,
  clientId: string,
  name: string,
  slug: string | null,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "update_client", {
    p_account_id: accountId,
    p_client_id: clientId,
    p_name: name,
    p_slug: slug,
  });
}

export async function archiveClient(
  client: SupabaseClient,
  accountId: string,
  clientId: string,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "archive_client", { p_account_id: accountId, p_client_id: clientId });
}

function siteParams(draft: SiteDraft): Record<string, unknown> {
  return {
    p_name: draft.name,
    p_street_address: draft.streetAddress,
    p_locality: draft.locality,
    p_region: draft.region,
    p_postal_code: draft.postalCode,
    p_timezone: draft.timezone,
    p_center_lat: draft.centerLat ?? null,
    p_center_lng: draft.centerLng ?? null,
  };
}

export async function createSite(
  client: SupabaseClient,
  accountId: string,
  clientId: string,
  draft: SiteDraft,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "create_site", {
    p_account_id: accountId,
    p_client_id: clientId,
    ...siteParams(draft),
  });
}

export async function updateSite(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  clientId: string,
  draft: SiteDraft,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "update_site", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_client_id: clientId,
    ...siteParams(draft),
  });
}

export async function archiveSite(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "archive_site", { p_account_id: accountId, p_site_id: siteId });
}

/**
 * Take a site live or pull it back to setup (G5 lifecycle gate). Archive is NOT a valid
 * status here - that flows through archiveSite so history/consequences are explicit.
 */
export async function setSiteLifecycleStatus(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  status: SiteLifecycleStatus,
): Promise<ClientSiteWriteOutcome> {
  return runWrite(client, "set_site_lifecycle_status", {
    p_account_id: accountId,
    p_site_id: siteId,
    p_status: status,
  });
}
